/**
 * The scope-summary API returns emissions in kg CO₂e — the base unit every
 * reported value is converted to before it is persisted. The dashboards display
 * tonnes instead: it is the GHG-reporting convention and keeps scope totals in a
 * readable range (28,137.485K kgCO₂e reads as 28,137.49 tCO₂e).
 */
export const EMISSION_UNIT = "tCO₂e";

const KG_PER_TONNE = 1000;

/**
 * Converts a kg CO₂e amount to tonnes.
 * @param {number|string} kg
 * @returns {number} tonnes CO₂e
 */
export const toTonnes = (kg) => Number(kg || 0) / KG_PER_TONNE;

/**
 * Formats a kg CO₂e amount as a localised tonnes figure. Amounts under 0.01 t
 * keep extra decimals so a tiny category still shows a value instead of "0".
 * @param {number|string} kg
 * @returns {string}
 */
export const formatTonnes = (kg) => {
  const tonnes = toTonnes(kg);
  const digits = tonnes !== 0 && Math.abs(tonnes) < 0.01 ? 4 : 2;
  return tonnes.toLocaleString(undefined, { maximumFractionDigits: digits });
};

/**
 * Converts chart rows from kg to tonnes, leaving the x-axis field verbatim.
 * @param {object[]} rows - one row per x value, all other fields numeric
 * @param {string} xKey - field to preserve, e.g. "month" or "category"
 * @returns {object[]}
 */
export const toTonnesRows = (rows = [], xKey = "month") =>
  rows.map((row) =>
    Object.keys(row).reduce(
      (out, key) => {
        if (key !== xKey) out[key] = toTonnes(row[key]);
        return out;
      },
      { [xKey]: row[xKey] },
    ),
  );
